function initProductCardSwatches(root) {
  root.querySelectorAll('[data-product-card-swatch]').forEach((swatch) => {
    if (swatch.dataset.productCardSwatchInitialized) return;
    swatch.dataset.productCardSwatchInitialized = 'true';

    swatch.addEventListener('click', () => {
      const card = swatch.closest('.product-card');
      const image = card ? card.querySelector('[data-product-card-image] img') : null;
      if (!image || !swatch.dataset.image) return;

      // A browser prefers `srcset` over `src` when both are present, so the
      // swap is invisible unless `srcset` is cleared first.
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = swatch.dataset.image;
    });
  });
}

class ProductCardQuickAdd {
  constructor(root) {
    this.root = root;
    this.bar = root.querySelector('[data-quick-add-open]');
    this.panel = root.querySelector('[data-quick-add-panel]');
    this.closeButton = root.querySelector('[data-quick-add-close]');

    const variantsEl = root.querySelector('[data-quick-add-variants]');
    this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    this.optionCount = Number(root.dataset.optionCount || 0);
    this.selectPlaceholder = root.dataset.selectPlaceholder || '';

    // Options with only one value stay pre-selected across resets - there's
    // no real choice to clear. Real choices (more than one value) do get
    // cleared, so re-opening after an add starts fresh instead of
    // instantly re-adding the previous pick.
    this.fixedPositions = new Set();
    for (let position = 1; position <= this.optionCount; position += 1) {
      const groupButtons = root.querySelectorAll(`[data-quick-add-position="${position}"]`);
      if (groupButtons.length === 1) this.fixedPositions.add(position);
    }

    // Position-keyed rather than color/size-specific, so this works for
    // any number and kind of option a product happens to have. Options
    // with only one value are pre-selected server-side (aria-current on
    // their sole button) - read that in here rather than guessing.
    this.selections = {};
    root.querySelectorAll('[data-quick-add-value]').forEach((button) => {
      if (button.getAttribute('aria-current') === 'true') {
        this.selections[Number(button.dataset.quickAddPosition)] = button.dataset.quickAddValue;
      }

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.select(Number(button.dataset.quickAddPosition), button);
      });
    });

    this.normalLabels = root.querySelectorAll('[data-quick-add-label-normal]');
    this.promptLabels = root.querySelectorAll('[data-quick-add-label-prompt]');
    this.updateLabels();
    this.updateAvailability();

    if (this.bar) {
      this.bar.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (this.tryAdd()) return;

        if (this.panel) this.root.dataset.open = 'true';
      });
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.reset();
      });
    }
  }

  select(position, button) {
    this.selections[position] = button.dataset.quickAddValue;

    this.root.querySelectorAll(`[data-quick-add-position="${position}"]`).forEach((other) => other.removeAttribute('aria-current'));
    button.setAttribute('aria-current', 'true');

    // The matrix isn't guaranteed dense (e.g. an ink color only exists
    // for some colors), so picking a value here can leave another,
    // already-selected field pointing at a combination that no longer
    // exists. Clear that other field rather than blocking this click -
    // blocking would trap the shopper on whatever color they picked
    // first. If clearing leaves only one valid choice for a field, just
    // resolve it automatically.
    this.clearConflictingSelections(position);
    this.autoSelectSoleRemainingValues();

    this.root.querySelectorAll('[data-quick-add-selected-value]').forEach((span) => {
      const spanPosition = Number(span.dataset.position);
      span.textContent = this.selections[spanPosition] || this.selectPlaceholder;
    });

    this.updateLabels();
    this.updateAvailability();
    this.tryAdd();
  }

  hasMatchFor(candidate) {
    return this.variants.some((item) => item.options.every((value, index) => candidate[index] === null || value === candidate[index]));
  }

  clearConflictingSelections(changedPosition) {
    for (let position = 1; position <= this.optionCount; position += 1) {
      if (position === changedPosition || this.fixedPositions.has(position)) continue;

      const value = this.selections[position];
      if (!value) continue;

      const candidate = [];
      for (let p = 1; p <= this.optionCount; p += 1) {
        if (p === changedPosition) candidate.push(this.selections[changedPosition]);
        else if (p === position) candidate.push(value);
        else candidate.push(null);
      }

      if (!this.hasMatchFor(candidate)) {
        delete this.selections[position];
        this.root.querySelectorAll(`[data-quick-add-position="${position}"]`).forEach((btn) => btn.removeAttribute('aria-current'));
      }
    }
  }

  autoSelectSoleRemainingValues() {
    for (let position = 1; position <= this.optionCount; position += 1) {
      if (this.selections[position]) continue;

      const buttons = Array.from(this.root.querySelectorAll(`[data-quick-add-value][data-quick-add-position="${position}"]`));
      const compatible = buttons.filter((button) => {
        const candidate = [];
        for (let p = 1; p <= this.optionCount; p += 1) {
          candidate.push(p === position ? button.dataset.quickAddValue : this.selections[p] || null);
        }
        return this.hasMatchFor(candidate);
      });

      if (compatible.length === 1) {
        this.selections[position] = compatible[0].dataset.quickAddValue;
        compatible[0].setAttribute('aria-current', 'true');
      }
    }
  }

  // Grays out (but never disables - see clearConflictingSelections) any
  // value that wouldn't combine with the current selections into a real
  // variant, so the sparse matrix is visible before a shopper picks it.
  updateAvailability() {
    this.root.querySelectorAll('[data-quick-add-value]').forEach((button) => {
      const position = Number(button.dataset.quickAddPosition);
      const candidate = [];
      for (let p = 1; p <= this.optionCount; p += 1) {
        candidate.push(p === position ? button.dataset.quickAddValue : this.selections[p] || null);
      }

      button.classList.toggle('is-unavailable', !this.hasMatchFor(candidate));
    });
  }

  // Once only one option is left unresolved, that's the one whose choice
  // triggers add-to-cart - call that out instead of the usual "Name - value".
  updateLabels() {
    const unresolvedPositions = [];
    for (let position = 1; position <= this.optionCount; position += 1) {
      if (!this.selections[position]) unresolvedPositions.push(position);
    }

    this.normalLabels.forEach((label) => {
      const isLastRemaining = unresolvedPositions.length === 1 && Number(label.dataset.position) === unresolvedPositions[0];
      label.hidden = isLastRemaining;
    });

    this.promptLabels.forEach((label) => {
      const isLastRemaining = unresolvedPositions.length === 1 && Number(label.dataset.position) === unresolvedPositions[0];
      label.hidden = !isLastRemaining;
    });
  }

  findMatch() {
    const options = [];
    for (let position = 1; position <= this.optionCount; position += 1) {
      options.push(this.selections[position] || null);
    }

    if (options.includes(null)) return null;
    return this.variants.find((item) => item.options.every((value, index) => value === options[index]));
  }

  tryAdd() {
    const variant = this.findMatch();
    if (!variant || !variant.available) return false;

    this.addVariant(variant.id);
    this.reset();
    return true;
  }

  reset() {
    delete this.root.dataset.open;
    this.clearSelections();
  }

  clearSelections() {
    this.root.querySelectorAll('[data-quick-add-value]').forEach((button) => {
      if (this.fixedPositions.has(Number(button.dataset.quickAddPosition))) return;
      button.removeAttribute('aria-current');
    });

    this.selections = {};
    this.root.querySelectorAll('[data-quick-add-value][aria-current="true"]').forEach((button) => {
      this.selections[Number(button.dataset.quickAddPosition)] = button.dataset.quickAddValue;
    });

    this.root.querySelectorAll('[data-quick-add-selected-value]').forEach((span) => {
      const position = Number(span.dataset.position);
      span.textContent = this.selections[position] || this.selectPlaceholder;
    });

    this.updateLabels();
    this.updateAvailability();
  }

  addVariant(variantId) {
    if (window.themeCartDrawer) window.themeCartDrawer.addVariant(variantId);
  }
}

function initProductCardQuickAdd(root) {
  root.querySelectorAll('[data-quick-add]').forEach((el) => {
    if (el.dataset.quickAddInitialized) return;
    el.dataset.quickAddInitialized = 'true';
    new ProductCardQuickAdd(el);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductCardSwatches(document);
  initProductCardQuickAdd(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initProductCardSwatches(event.target);
  initProductCardQuickAdd(event.target);
});
